<script setup lang="ts">
import { ref, computed, reactive, onMounted, getCurrentInstance } from 'vue';
import { useI18n } from '@/plugins/i18n';
import { reportingService } from '@/common/reporting';
import { GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import * as PleasedApi from '@/common/api/api.pleased';
import { MIN_DESCRIPTION_CHAR_NUM, MAX_DESCRIPTION_CHAR_NUM, MAX_GUEST_FIELDS_CHAR } from '@/common/config';
import { isEmailValid, replacePlaceholders } from '@/common/utils';
import { store } from '@/store';
import type { FormField } from '@/common/types';
import Api from '@/common/api';
import {
  getRequestFields,
  type TicketForm as LoggedInTicketForm,
} from '@/views/Chatroom/RuleEngineHelper/LoggedInForm/LoggedInForm.utils';
import { getUserSelections, messageStore } from '@/common/bot/flow-frameworks/messages';
import { UserSelection } from '@/common/api/models.pleased';
import { BotConstants } from '@/common/bot/lib';
import { UserTransactionMessage } from '@/common/bot/types';
import { analytics, CRITICAL_EVENTS } from '@/common/logs';
import { BTN_EVTS } from '@/directives/trackClick';
import { parseErrorMessage } from '@/common/error';
import PlsDialog, { type PlsDialogAction } from '@/components/PlsDialog.vue';
import ValidationInput from '@/components/ValidationInput.vue';
import { useTicketSubject, type SelectedOptions } from './useTicketSubject';
import type { TriggerMatch } from '@/stores/useDialogTrigger';
import * as env from '@/common/environment';
import { ReloadActiveConversationBus } from '@/common/eventBus';
import { useConnectConfig } from '@/stores/useConnectConfig';
import { storeToRefs } from 'pinia';
import { useOpenRequestCustomisation } from '@/views/ChatWidget/composables/useOpenRequestCustomisation';
import { useGuestFieldRequirements } from '@/views/ChatWidget/composables/guest-form/useGuestFormCustomisation';
import GuestFormFields from './GuestFormFields.vue';
import type { GuestFieldErrors, OpenRequestModalFormState } from './openRequestForm.types';
import { useBotUser } from '@/stores/useBotUser';
import { useGuestTicketFormValidation } from '@/composables/guest-form';
import * as BridgeEvents from '@/common/bridge/modern-bridge/bridge-events';
import { useOpenRequestModalUserProps } from './useOpenRequestModalUserProps';

interface OpenRequestModalProps {
  trigger: TriggerMatch;
  selectedOptions: SelectedOptions;
}

const props = defineProps<OpenRequestModalProps>();
const openRequestUser = useOpenRequestModalUserProps();

const emit = defineEmits<{
  (e: 'end-chat'): void;
  (e: 'restart-chat'): void;
  (e: 'go-to-my-request'): void;
}>();

const { t } = useI18n();

const { customisation: textSubmitCustomisation, placeholderText: textSubmitPlaceholder } =
  useOpenRequestCustomisation();
const botUserStore = useBotUser();
const guestFieldRequirements = useGuestFieldRequirements();
const { validateGuestTicketForm, fieldErrorMessage: mapGuestTicketFieldError } = useGuestTicketFormValidation();
const connectConfigStore = useConnectConfig();
const { connectConfig } = storeToRefs(connectConfigStore);

const enterDataStep = ref(false);
const openRequestResponse = ref<string>('');
const openRequestCreated = ref(false);
const openRequestError = ref(false);
const browseOtherTopics = ref(false);
const creatingTicket = ref(false);
const userSelections = ref<UserSelection[]>([]);

const apiLoggedInFormFields = ref<FormField[]>([]);

const form = reactive<OpenRequestModalFormState>({
  name: '',
  phone: '',
  email: '',
  description: '',
  subject: '',
});

const fieldErrors = reactive<Record<string, string>>({
  name: '',
  phone: '',
  email: '',
  description: '',
  subject: '',
});

const formVModel = computed({
  get: (): OpenRequestModalFormState => form,
  set(v: OpenRequestModalFormState) {
    Object.assign(form, v);
  },
});

const { ticketSubject } = useTicketSubject(props.trigger.matchSource, props.selectedOptions);

const isLoggedIn = computed(() => openRequestUser.value.isLoggedIn);
const features = computed(() => env.get('features'));

const showDescriptionBox = computed(
  () =>
    Boolean(features.value?.chatTriggerOpenRequestDescriptionBox) &&
    !openRequestCreated.value &&
    !openRequestError.value
);

const guestTicketValidationCtx = computed(() => ({
  showDescriptionBox: showDescriptionBox.value,
  minDescription: MIN_DESCRIPTION_CHAR_NUM,
  maxDescription: MAX_GUEST_FIELDS_CHAR.description,
  nameOptional: guestFieldRequirements.nameOptional.value,
  emailOptional: guestFieldRequirements.emailOptional.value,
  descriptionOptional: guestFieldRequirements.descriptionOptional.value,
  phoneOptional: guestFieldRequirements.phoneOptional.value,
}));

const ticketDescription = computed(() => {
  if (features.value?.chatTriggerOpenRequestDescriptionBox) {
    return form.description ?? '';
  }
  return ticketSubject.value;
});

const staticLoggedInFormFields = computed((): FormField[] => [
  {
    id: -10000,
    displayName: t('global.description') as string,
    placeholder: t('global.describe') as string,
    name: 'description',
    required: true,
  },
]);

const allFormFields = computed(() => [...staticLoggedInFormFields.value, ...apiLoggedInFormFields.value]);

const guestFieldErrorsView = computed(
  (): GuestFieldErrors => ({
    name: fieldErrors.name,
    phone: fieldErrors.phone,
    email: fieldErrors.email,
    description: fieldErrors.description,
  })
);

const descriptionHintForGuest = computed(
  () => connectConfig.value?.descriptionMessage ?? (t('global.describe') as string)
);

const content = computed(() => {
  if (openRequestError.value) {
    const middleErrorTextKey = openRequestUser.value.isLoggedIn
      ? 'global.ticket.ticket_creation_failed.logged_in_suggestion'
      : 'global.ticket.ticket_creation_failed.guest_suggestion';
    const errorText = `${t('global.ticket.ticket_creation_failed.start')} ${t(middleErrorTextKey)} ${t(
      'global.ticket.ticket_creation_failed.end'
    )}`;

    return errorText;
  }

  return (
    openRequestResponse.value ||
    replacePlaceholders(props.trigger.message, {
      requester: {
        foreignId: openRequestUser.value.requesterForeignId,
      },
    })
  );
});

const dialogActions = computed<PlsDialogAction[]>(() => {
  if (enterDataStep.value) {
    return [
      {
        id: 'back',
        text: t('global.back') as string,
        color: 'secondary',
        action: onBackClick,
      },
      {
        id: 'proceed',
        text: t('global.proceed') as string,
        color: 'primary',
        action: onProceedClick,
      },
    ];
  }

  if (openRequestError.value) {
    return [
      {
        id: 'end-chat',
        text: t('global.end_chat') as string,
        color: 'primary',
        action: endChat,
      },
    ];
  }

  if (browseOtherTopics.value) {
    return [
      {
        id: 'restart-chat',
        text: t('chat.sys.navigation.restart_chat') as string,
        color: 'secondary',
        action: restartChat,
      },
      {
        id: 'end-chat',
        text: t('global.end_chat') as string,
        color: 'primary',
        action: endChat,
      },
    ];
  }

  if (openRequestCreated.value) {
    return [
      {
        id: 'go-to-my-request',
        text: t('page.resumable_chat.confirm.btn.go_to_my_request') as string,
        color: 'primary',
        action: goToMyRequest,
      },
      {
        id: 'end-chat',
        text: t('global.end_chat') as string,
        color: 'secondary',
        action: endChat,
      },
      {
        id: 'browse-other-topics',
        text: t('global.browse_other_topics') as string,
        color: 'text',
        action: onBrowseOtherTopicsClick,
      },
    ];
  }

  return [
    {
      id: 'open-request',
      text: t('global.open_request') as string,
      color: 'secondary',
      action: onOpenRequestClick,
    },
    {
      id: 'end-chat',
      text: t('global.end_chat') as string,
      color: 'primary',
      action: endChat,
    },
  ];
});

const displayActions = computed(() => dialogActions.value);

const dialogTitle = computed(() => {
  if (enterDataStep.value) {
    return t('page.guest.fill_details') as string;
  }
  return undefined;
});

const messageList = computed(() => messageStore.get());

const issueMsg = computed(() => {
  const message = messageList.value.find((m) => m.id === BotConstants.IDS.USER_ISSUE_LIST) as UserTransactionMessage;
  if (message) {
    return message.issues.join(' / ');
  }
  return '';
});

const clearGuestFieldError = (field: keyof GuestFieldErrors) => {
  fieldErrors[field] = '';
};

const trackOpenRequestCreationStart = () => {
  const proxy = getCurrentInstance()?.proxy as { $gtag?: { event: (name: string) => void } } | undefined;
  proxy?.$gtag?.event(GA_EVENT_NAME_ENUM.START_OPEN_REQUEST_CREATION);
};

const trackModalButton = (name: (typeof BTN_EVTS)[keyof typeof BTN_EVTS]) => {
  analytics.trackClick({
    name,
    selection: props.trigger.chatTriggerId,
  });
};

const clearOpenRequestFieldErrors = () => {
  (Object.keys(fieldErrors) as (keyof typeof fieldErrors)[]).forEach((k) => {
    fieldErrors[k] = '';
  });
};

const validateLoggedInOpenRequestForm = (): boolean => {
  let result = true;

  const isDescriptionRequired = !!allFormFields.value.find(
    (item) => item.name === 'description' && item.required === true
  );
  const isEmailRequired = !!allFormFields.value.find((item) => item.name === 'email' && item.required === true);

  const textLength = (form.description ?? '').length;

  if (
    isDescriptionRequired &&
    form.description !== undefined &&
    (textLength < MIN_DESCRIPTION_CHAR_NUM || textLength > MAX_GUEST_FIELDS_CHAR.description)
  ) {
    if (textLength > MAX_GUEST_FIELDS_CHAR.description) {
      fieldErrors.description = t('page.guest.description.exceed', {
        maxChar: MAX_GUEST_FIELDS_CHAR.description,
      }) as string;
    } else {
      fieldErrors.description = t('page.guest.description.invalid') as string;
    }
    result = false;
  }
  if (isEmailRequired && !isEmailValid(form.email ?? '')) {
    fieldErrors.email = t('page.guest.email.invalid') as string;
    result = false;
  }

  return result;
};

const validateGuestOpenRequestModalForm = async (): Promise<boolean> => {
  const { valid, errors } = await validateGuestTicketForm(form, guestTicketValidationCtx.value);
  fieldErrors.name = mapGuestTicketFieldError(errors.name);
  fieldErrors.phone = mapGuestTicketFieldError(errors.phone);
  fieldErrors.email = mapGuestTicketFieldError(errors.email);
  fieldErrors.description = mapGuestTicketFieldError(errors.description);
  return valid;
};

async function openRequest() {
  if (creatingTicket.value) return;
  creatingTicket.value = true;

  const ticketFields = getRequestFields({
    form: form as LoggedInTicketForm,
    allFields: allFormFields.value,
    issueMsg: issueMsg.value,
    userEmail: openRequestUser.value.accountEmail,
    userPhone: openRequestUser.value.accountPhone,
  })
    .filter((f) => f.id > 0)
    .map((f) => ({ fieldId: f.id, fieldValue: f.value }));

  reportingService.openRequestClicked();
  trackOpenRequestCreationStart();

  const payload = {
    chatTriggerId: props.trigger.chatTriggerId,
    subject: ticketSubject.value,
    description: ticketDescription.value,
    recipientAddress: form.email || openRequestUser.value.pleasedUserEmail,
    ticketFields,
    userSelections: userSelections.value,
    userLogs: reportingService.getLogs(),
    chatbotSessionId: analytics.sessionId,
  };

  try {
    const result = await PleasedApi.createTicket(payload);

    const responseText =
      result.chatTriggerResponse ||
      (t('global.ticket.ticket_created', {
        ticketId: result.id,
      }) as string);

    openRequestResponse.value = replacePlaceholders(
      responseText,
      {
        requester: {
          name: form.name,
          phone: form.phone,
          email: form.email ?? '',
          foreignId: openRequestUser.value.pleasedUserForeignId,
        },
        ticketId: result.id,
      },
      {
        requester: {
          name: 'User',
          phone: '',
          email: '',
          foreignId: '',
        },
      }
    );

    openRequestCreated.value = true;
    BridgeEvents.emitOpenRequestCreated(result, {
      description: ticketDescription.value,
      chatbotSessionId: analytics.sessionId,
    });
    props.trigger.report.openRequest(Boolean(result.isCreated));
    ReloadActiveConversationBus.pub();
  } catch (error) {
    analytics.trackCriticalEvent({
      name: CRITICAL_EVENTS.CONNECT_OPTIONS_OPEN_REQUEST_INITIATED_ERROR,
      metadata: {
        error: parseErrorMessage(error),
        payload,
      },
    });

    openRequestError.value = true;
  }
  creatingTicket.value = false;
  enterDataStep.value = false;
}

async function onProceedClick() {
  if (creatingTicket.value) {
    return;
  }

  trackModalButton(BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_PROCEED);

  if (openRequestUser.value.isLoggedIn) {
    form.description = form.description.trim();
  }

  clearOpenRequestFieldErrors();

  if (openRequestUser.value.isLoggedIn) {
    if (!validateLoggedInOpenRequestForm()) {
      return;
    }
  } else if (!(await validateGuestOpenRequestModalForm())) {
    return;
  }

  if (!openRequestUser.value.isLoggedIn) {
    const userData = await PleasedApi.getAnonymousToken();
    const config = {
      ...userData,
      isGuest: true,
      name: form.name || userData.name || '',
      email: form.email || userData.email || '',
      phone: form.phone || userData.phone || '',
    };
    store.commit('user/updateGuestUser', config);
    store.commit('user/updatePleasedUser', config);
    botUserStore.setGuestUser(config);
  }

  await openRequest();
}

function onBackClick() {
  trackModalButton(BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_BACK);
  enterDataStep.value = false;
}

const handleLoggedInDescriptionInput = (value: string) => {
  form.description = value;
};

const handleEnterDataStepKeydown = (event: KeyboardEvent) => {
  if (!isLoggedIn.value || creatingTicket.value) {
    return;
  }
  if (event.key === 'Enter' && event.ctrlKey) {
    event.preventDefault();
    onProceedClick();
  }
};

function onOpenRequestClick() {
  trackModalButton(BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_CLICKED);
  enterDataStep.value = true;
}

function onBrowseOtherTopicsClick() {
  trackModalButton(BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_BROWSE_OTHER_TOPICS);
  browseOtherTopics.value = true;
  openRequestCreated.value = false;
}

function restartChat() {
  trackModalButton(BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_RESTART_CHAT);
  emit('restart-chat');
}

function goToMyRequest() {
  trackModalButton(BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_GO_TO_MY_REQUESTS);
  emit('go-to-my-request');
}

function endChat() {
  trackModalButton(BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_END_CHAT);
  emit('end-chat');
}

onMounted(() => {
  userSelections.value = getUserSelections(messageList.value);

  const g = openRequestUser.value.guestFormDraft;
  if (g && !openRequestUser.value.isLoggedIn) {
    if (g.name) {
      form.name = g.name;
    }
    if (g.email) {
      form.email = g.email;
    }
    if (g.phone) {
      form.phone = g.phone;
    }
    if (g.description) {
      form.description = g.description;
    }
  }

  if (openRequestUser.value.isLoggedIn) {
    const formFieldRequest = PleasedApi.getTicketFields().then((res) =>
      res.map((f) => ({ ...f, required: f.isMandatory, name: f.fieldName })).filter((item) => item.name !== 'email')
    );

    Promise.all([Api.getAccountInfo(), formFieldRequest]).then(([res, formFields]) => {
      apiLoggedInFormFields.value = formFields;
      if (res.bizCode === 10000) {
        if (res.data.email) {
          store.commit('user/setUserPhoneOrEmailIfNotExists', {
            email: res.data.email,
          });
        }
        if (res.data.phone) {
          store.commit('user/setUserPhoneOrEmailIfNotExists', {
            phone: res.data.phone,
          });
        }
      }

      if (res.bizCode === 10000 && res.data.email) {
        form.email = res.data.email;
      }

      form.subject = ticketSubject.value;
    });
  }
});
</script>

<template>
  <PlsDialog :open="true" :title="dialogTitle" :actions="displayActions">
    <template #default>
      <div v-if="enterDataStep" class="textarea-container" @keydown="handleEnterDataStepKeydown">
        <ValidationInput
          v-if="isLoggedIn"
          tag="textarea"
          :value="form.description"
          :placeholder="textSubmitPlaceholder"
          :max-length="MAX_DESCRIPTION_CHAR_NUM"
          :disabled="creatingTicket"
          :show-error-text-block="false"
          :customisation="textSubmitCustomisation?.input"
          class="open-request-modal__description-input"
          @input="handleLoggedInDescriptionInput"
        />
        <GuestFormFields
          v-else
          v-model="formVModel"
          :field-errors="guestFieldErrorsView"
          :show-description="showDescriptionBox"
          :description-hint="descriptionHintForGuest"
          @clear-field-error="clearGuestFieldError"
        />
      </div>

      <div v-else class="text-content theme-font-main">
        {{ content }}
      </div>
    </template>
  </PlsDialog>
</template>

<style scoped lang="scss">
.textarea-container {
  width: 100%;
}

.open-request-modal__description-input {
  width: 100%;
}

.text-content {
  text-align: left;
  white-space: pre-wrap;
  line-height: 1.5;
}
</style>
